import {
  Flex,
  Text,
  Icon,
  Box,
  InputGroup,
  InputLeftElement,
  Input,
  InputRightElement,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { FormFieldContainer } from '../../components';
import { AiPrompt } from './AiPrompt';
import { EditorBubbleMenu } from './EditorBubbleMenu';
import { generateAiContent } from './aiApiCalls';
import { Star06 } from '@kula-ai/icons/solid/Star06';
import { useShowToast } from '@/hooks';
import { CopyFromModal } from '@/components/ats';
// import { Eye } from '@kula-ai/icons/dual-tone/Eye';
import { Upload03 } from '@kula-ai/icons/solid/Upload03';
import { AiLoader } from '@/assets/icons/ai';
import { atsJobsApiActions } from '@/store/atsJobs';
import { useDispatch } from 'react-redux';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';

function cleanupHTML(htmlString) {
  // Parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Function to clean a node and its children
  function cleanNode(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      // Remove empty paragraph tags
      if (node.tagName === 'P' && !node.textContent.trim()) {
        node.parentNode.removeChild(node);
        return;
      }

      // Remove paragraph tags around headings
      if (
        node.tagName === 'P' &&
        node.children.length === 1 &&
        node.firstElementChild &&
        /^H[1-6]$/.test(node.firstElementChild.tagName)
      ) {
        node.parentNode.replaceChild(node.firstElementChild, node);
        return;
      }

      // Remove paragraph tags inside list items
      if (
        node.tagName === 'LI' &&
        node.children.length === 1 &&
        node.firstElementChild &&
        node.firstElementChild.tagName === 'P'
      ) {
        node.innerHTML = node.firstElementChild.innerHTML;
      }

      // Clean child nodes
      Array.from(node.childNodes).forEach(cleanNode);
    }
  }

  // Clean the entire document body
  cleanNode(doc.body);

  // Remove any remaining empty paragraphs
  doc.body.querySelectorAll('p').forEach((p) => {
    if (!p.textContent.trim()) {
      p.parentNode.removeChild(p);
    }
  });

  // Add <br> after each remaining paragraph
  doc.body.querySelectorAll('p').forEach((p) => {
    const br = doc.createElement('p');
    if (p.nextSibling) {
      p.parentNode.insertBefore(br, p.nextSibling);
    } else {
      p.parentNode.appendChild(br);
    }
  });

  // Get the cleaned HTML
  let cleanHtml = doc.body.innerHTML;

  return cleanHtml.trim();
}

export const EditorWithAi = ({
  formik,
  uiConfig = {},
  apiConfig = {},
  defaultValue = '',
  editorConfig,
  setEditorConfig,
}) => {
  const [promptValue, setPromptValue] = useState('');
  const toast = useShowToast();
  const dispatch = useDispatch();
  const { jobPermissions } = useAtsPermissions();

  const { values: { text = '' } = {} } = formik;
  const inputRef = useRef(undefined);
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [BubbleConfig, setBubbleConfig] = useState(undefined);
  const modalActions = useDisclosure();

  const { getJobInfo, urls = {} } = apiConfig;
  const { job_meta = '', job_id, job_template } = getJobInfo(formik?.values);

  useEffect(() => {
    if (defaultValue) {
      setEditorConfig((prev) => ({
        ...prev,
        content: defaultValue,
        replaceContent: false,
        show: true,
        isAi: defaultValue ? true : false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const validate = (value, item) => {
    const removeHtmlTags = (str) => str?.replace(/<[^>]*>?/gm, '');
    const plainTextValue = removeHtmlTags(value || editorConfig?.content);
    return plainTextValue?.length > 100 || item?.isRequired === false
      ? undefined
      : `${item?.label} is required and  must be at least 100 characters long`;
  };

  const editorField = {
    //since BE using text for this
    name: 'text',
    label: 'Description',
    type: 'editor',
    editable: jobPermissions?.update,
    isRequired: true,
    containerStyles: {
      minW: '695px',
      color: 'base-black',
      sx: {
        '& .editor-style p': {
          color: 'base-black',
        },
      },
    },
    editorStyles: {
      height: '400px',
    },
    content: editorConfig?.content,
    replaceContent: editorConfig?.replaceContent,
    toolBarProps: {
      top: {
        position: 'sticky',
        top: '-32px',
      },
    },
    renderAdditionalContent: (props) => {
      return (
        <EditorBubbleMenu
          enabled={true}
          setBubbleConfig={setBubbleConfig}
          aiMessageGeneration={aiMessageGeneration}
          apiConfig={{ urls, ...getJobInfo(formik?.values) }}
          {...props}
        />
      );
    },
  };

  const aiMessageGeneration = async (
    url,
    payload,
    isAi = true,
    replaceContent = false
  ) => {
    setEditorConfig((prev) => ({
      ...prev,
      replaceContent,
      isAiLoading: true,
    }));

    try {
      const finalResult = await generateAiContent(
        url,
        payload,
        urls?.key,
        toast,
        setEditorConfig
      );
      const { generated_message = {} } = finalResult?.data || {};
      const { message = '', status } = generated_message;

      const editorMessage = message?.replaceAll('\n', '');
      const cleanedMessage = cleanupHTML(editorMessage);

      if (status === 'success') {
        setEditorConfig({
          isAi: isAi,
          show: true,
          content: cleanedMessage,
          isAiLoading: false,
          replaceContent,
        });
        return message;
      }
    } catch (error) {
      setEditorConfig((prev) => ({
        ...prev,
        replaceContent,
        isAiLoading: false,
      }));
      console.error('Error:', error);
    }
  };

  const handlePromptEdit = async () => {
    if (inputRef?.current?.value?.length === 0) {
      return null;
    }
    const payload = {
      job_meta,
      job_id,
      input_content: text,
      user_prompt: inputRef?.current?.value,
      tone: 'professional',
    };
    try {
      setIsLoading(true);
      const message = await aiMessageGeneration(
        urls?.promptEdit,
        payload,
        true
      );
      if (message && inputRef?.current) inputRef.current.value = '';
    } finally {
      setIsLoading(false);
    }
  };

  const renderAfterEditor = () => {
    const { isAi } = editorConfig || {};
    if (isAi) {
      return (
        <InputGroup borderColor="ats-primary" focusBorderColor="ats-primary">
          <InputLeftElement pointerEvents="none">
            <Icon as={Star06} color="ats-primary" />
          </InputLeftElement>
          <Input
            type="text"
            ref={inputRef}
            disabled={!jobPermissions?.update}
            _focus={{ borderColor: '#605AFB !important' }}
            borderColor="#605AFB !important"
            placeholder="Ask AI to edit"
            isDisabled={editorConfig?.isAiLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handlePromptEdit();
              }
            }}
          />{' '}
          <InputRightElement width="2rem" cursor="pointer">
            <Icon
              as={Upload03}
              onClick={handlePromptEdit}
              color="ats-primary"
              w="1.25em"
              h="1.25em"
            />
          </InputRightElement>
        </InputGroup>
      );
    }

    return (
      <Flex w="100%" gap=".5rem" cursor="pointer">
        <Icon as={Star06} color="ats-primary" />
        <Text
          onClick={() => {
            setPromptValue('');
            setEditorConfig((prev) => ({
              ...prev,
              isAi: false,
              show: false,
            }));
          }}
          size="sm-medium"
          color="ats-primary"
        >
          Switch to AI
        </Text>
      </Flex>
    );
  };

  const renderDefault = () => {
    return (
      <Flex flexDir="column">
        <Flex
          flexDir="column"
          w="100%"
          // alignItems="center"
          // justifyContent="center"
          p={editorConfig?.show ? '0' : '3em 8em'}
        >
          {editorConfig?.show ? (
            <Box>
              <Flex
                w="100%"
                alignItems="center"
                justifyContent="flex-end"
                gap="1.563em"
              >
                <CopyFromModal
                  isDisabled={
                    editorConfig?.isAiLoading || !jobPermissions?.update
                  }
                  label={'Copy from another job'}
                  type="jd"
                  modalActions={modalActions}
                  apiCall={() =>
                    dispatch(
                      atsJobsApiActions('getAllJobs', {
                        requestConfig: {
                          params: {
                            view: 'job_description',
                            limit: 500,
                            'sort_by[open_at]': 'DESC',
                          },
                        },
                      })
                    )
                  }
                  modalConfig={{
                    modalAction: (content, onClose) => {
                      setEditorConfig((prev) => ({
                        ...prev,
                        content,
                        replaceContent: true,
                      }));
                      formik?.setFieldValue('text', content);
                      onClose();
                    },
                  }}
                />
                {/* todo: enable if wanted in future
							 <Flex
                gap="0.313em"
                alignItems="center"
                cursor={editorConfig?.isAiLoading ? 'not-allowed' : 'pointer'}
                opacity={editorConfig?.isAiLoading ? 0.5 : 1}
              >
                <Icon as={Eye} color="ats-primary" />
                <Text size="sm-medium" color="ats-primary">
                  Preview
                </Text>
              </Flex> */}
              </Flex>
              <FormFieldContainer
                formik={formik}
                item={editorField}
                validate={validate}
              />
              {renderAfterEditor()}
            </Box>
          ) : (
            <AiPrompt
              uiConfig={uiConfig}
              editorConfig={editorConfig}
              setEditorConfig={setEditorConfig}
              promptValue={promptValue}
              setPromptValue={setPromptValue}
              onSubmit={() =>
                // first time generation sequence
                aiMessageGeneration(
                  urls?.generate,
                  {
                    job_meta,
                    job_id,
                    job_template,
                    tone: 'professional',
                    user_prompt: promptValue,
                  },
                  true,
                  true
                )
              }
            />
          )}
        </Flex>
      </Flex>
    );
  };
  return (
    <Flex minH="30vh" alignItems="center" justifyContent="center">
      {isLoading ? (
        <Flex flexDir="column" gap=".5em">
          <Icon as={AiLoader} w="100px" h="100px" />
          <Text
            size="xxl-regular"
            style={{
              background:
                '-webkit-linear-gradient(91.04deg, #7CD0FF 5.69%, #6249F9 88.5%)',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            AI is Writing
          </Text>
        </Flex>
      ) : (
        renderDefault()
      )}
    </Flex>
  );
};
